import { useEffect, useRef, useState, useCallback } from 'react';
import { ToolHandler } from 'openai-realtime-api';
import {
  ConversationHistory,
  createHistory,
  addMessage,
  updateMessage,
  Message,
  fromApiMessage,
  toApiMessage,
  isTextContent,
  isAudioContent,
  isFunctionCallContent,
  TextContent,
  AudioContent
} from '../types/history';
import { SessionConfig as SessionConfigType } from '../config/realtimeConfig';
import { tocho, tocho1 } from '../config/tocho';

const API_BASE_URL = 'https://api.openai.com/v1/realtime';

export interface RealtimeEvent {
  time: string;
  source: 'client' | 'server';
  count?: number;
  event: {
    event_id?: string;
    type?: string;
    [key: string]: any;
  };
}

export function useRealtimeWebRTC(
  startTimeRef: React.RefObject<string>,
  initialInstructions: string,
  audioRef: React.RefObject<HTMLAudioElement>,
  tools?: Array<{ schema: any; fn: ToolHandler }>,
  onLocalStream?: (stream: MediaStream) => void,
  onRemoteStream?: (stream: MediaStream) => void,
  sessionConfig?: Partial<SessionConfigType>
) {
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const messageEventMap = useRef<Map<string, string>>(new Map());
  const initialHistoryIdsRef = useRef<Set<string>>(new Set());
  const audioBufferRef = useRef<string>('');
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [history, setHistory] = useState<ConversationHistory>(() => 
    createHistory(sessionConfig || {})
  );
  const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);

  const sendEvent = useCallback((event: any) => {
    if (dataChannelRef.current?.readyState !== 'open') {
      console.warn('Cannot send event: data channel not open');
      return;
    }
    
    const eventId = crypto.randomUUID();
    const now = new Date().toISOString();
    const eventWithId = { 
      ...event, 
      event_id: eventId
    };
    
    console.group(`[Event ${eventId}]`);
    console.log('Type:', event.type);
    console.log('Data:', eventWithId);
    console.groupEnd();
    dataChannelRef.current.send(JSON.stringify(eventWithId));

    setRealtimeEvents(prev => [...prev, {
      time: now,
      source: 'client',
      event: eventWithId
    }]);
  }, []);

  const loadHistory = useCallback((loadedHistory: ConversationHistory) => {
    if (!dataChannelRef.current) {
      console.log(isConnected)
      console.warn('Cannot load history: not connected');
      return;
    }

    // Track which messages are part of the initial history load
    initialHistoryIdsRef.current = new Set(loadedHistory.messages.map(msg => msg.id));

    // Create conversation items for each message
    loadedHistory.messages.forEach((msg, index) => {
      const content = (() => {
        if (msg.role === 'user') {
          if (isAudioContent(msg.content)) {
            return [{
              type: 'input_text',
              text: msg.content.transcript || ''
            }];
          }
          return [{
            type: 'input_text',
            text: isTextContent(msg.content) ? msg.content.text : ''
          }];
        }
        if (msg.role === 'assistant') {
          if (isAudioContent(msg.content)) {
            return [{
              type: 'text',
              text: msg.content.transcript || ''
            }];
          }
          return [{
            type: 'text',
            text: isTextContent(msg.content) ? msg.content.text : ''
          }];
        }
        if (msg.role === 'system') {
          return [{
            type: 'input_text',
            text: isTextContent(msg.content) ? msg.content.text : ''
          }];
        }
        return [];
      })();

      const createEvent: any = {
        type: 'conversation.item.create',
        event_id: crypto.randomUUID(),
        previous_item_id: index > 0 ? loadedHistory.messages[index - 1].id : null,
        item: {
          id: msg.id,
          type: msg.type,
          role: msg.role,
          content,
          status: 'completed' as const
        }
      };

      if (msg.type === 'function_call' && isFunctionCallContent(msg.content)) {
        createEvent.item = {
          ...createEvent.item,
          call_id: msg.tool_call_id,
          name: msg.content.name,
          arguments: msg.content.arguments
        };
      }

      sendEvent(createEvent);
    });
  }, [sendEvent]);

  function handleFunctionCall(
    dc: RTCDataChannel, 
    event: any, 
    tools?: Array<{ schema: any; fn: ToolHandler }>,
    onResult?: (result: unknown) => void
  ) {
    const tool = tools?.find(t => t.schema.name === event.name);
    if (!tool) {
      console.warn(`No tool found for function call: ${event.name}`);
      return;
    }

    const eventId = event.event_id || crypto.randomUUID();
    console.log(`[Event ${eventId}] Executing function ${event.name} with args:`, event.arguments);
    const args = JSON.parse(event.arguments);
    
    tool.fn(args)
      .then((result: unknown) => {
        console.log(`[Event ${eventId}] Function ${event.name} result:`, result);
        onResult?.(result);
        
        dc.send(JSON.stringify({
          type: 'conversation.item.create',
          event_id: eventId,
          item: {
            type: 'function_call_output',
            call_id: event.call_id,
            output: JSON.stringify(result)
          }
        }));

        dc.send(JSON.stringify({
          type: 'response.create',
          event_id: eventId
        }));
      })
      .catch((error: Error) => {
        console.error(`[Event ${eventId}] Error executing function ${event.name}:`, error);
      });
  }

  const handleEvent = useCallback((e: MessageEvent) => {
    const event = JSON.parse(e.data);
    const eventId = event.event_id || crypto.randomUUID();
    const now = new Date().toISOString();
    
    console.group(`[Event ${eventId}]`);
    console.log('Type:', event.type);
    console.log('Data:', event);
    console.groupEnd();

    setRealtimeEvents(prev => [...prev, {
      time: now,
      source: 'server',
      event: {
        ...event,
        event_id: eventId
      }
    }]);

    // Handle input audio buffer events
    if (event.type === 'input_audio_buffer.append' && 'audio' in event) {
      audioBufferRef.current += event.audio as string;
    }
    else if (event.type === 'input_audio_buffer.committed') {
      console.log(`[Event ${eventId}] Input audio buffer committed`);
      const audioData = audioBufferRef.current;
      audioBufferRef.current = '';
      
      if (audioData) {
        setHistory(prev => {
          const message = addMessage(prev, {
            type: 'message',
            role: 'user',
            status: 'completed' as const,
            content: {
              type: 'audio',
              audio: audioData,
              transcript: ''
            }
          });
          if ('item_id' in event) {
            messageEventMap.current.set(event.item_id as string, message.messages[message.messages.length - 1].id);
          }
          return message;
        });
      }
    }
    // Handle conversation item creation
    else if (event.type === 'conversation.item.created') {
      console.log(`[Event ${eventId}] Item created:`, event.item);
      // Skip adding messages that are part of the initial history load
      if (event.item.type === 'message' && !initialHistoryIdsRef.current.has(event.item.id)) {
        setHistory(prev => {
          // For assistant audio messages
          if (event.item.role === 'assistant' && event.previous_item_id) {
            const content: AudioContent = {
              type: 'audio',
              audio: '',
              transcript: ''
            };
            const newMessage = addMessage(prev, {
              type: 'message',
              role: 'assistant',
              status: 'in_progress' as const,
              content
            });
            // Use the API's item ID to map to our internal message ID
            const internalMsgId = newMessage.messages[newMessage.messages.length - 1].id;
            messageEventMap.current.set(event.item.id, internalMsgId);
            console.log(`[Event ${eventId}] Mapping API item ${event.item.id} to internal message ${internalMsgId}`);
            console.log(`[Event ${eventId}] Created assistant message ${internalMsgId} for item ${event.item.id}`);
            return newMessage;
          }
          // For user audio messages
          else if (event.item.role === 'user' && event.item.content && Array.isArray(event.item.content)) {
            const content = event.item.content[0];
            if (content.type === 'input_audio' || content.type === 'audio') {
              const message = addMessage(prev, {
                type: 'message',
                role: 'user',
            status: 'completed' as const,
            content: {
                  type: 'audio',
                  audio: content.audio || '',
                  transcript: content.transcript || ''
                }
              });
              messageEventMap.current.set(event.item.id, message.messages[message.messages.length - 1].id);
              return message;
            }
            // For user text messages
            else if (content.type === 'input_text') {
              const message = addMessage(prev, {
                type: 'message',
                role: 'user',
            status: 'completed' as const,
            content: {
              type: 'input_text',
                  text: content.text
                }
              });
              messageEventMap.current.set(event.item.id, message.messages[message.messages.length - 1].id);
              return message;
            }
          }
          // For non-audio messages
          return addMessage(prev, fromApiMessage(event.item));
        });
      }
    }
    // Handle text responses
    else if (event.type === 'response.text.delta') {
      setHistory(prev => {
        const lastMessage = prev.messages[prev.messages.length - 1];
        if (!lastMessage || lastMessage.role !== 'assistant' || !isTextContent(lastMessage.content)) {
          console.log(`[Event ${eventId}] Creating new assistant message with text:`, event.delta);
          return addMessage(prev, {
            type: 'message',
            role: 'assistant',
            status: 'in_progress' as const,
            content: {
              type: 'text',
              text: event.delta
            }
          });
        }
        console.log(`[Event ${eventId}] Updating assistant message with text:`, event.delta);
        return updateMessage(prev, lastMessage.id, {
          content: {
            type: 'text' as const,
            text: (lastMessage.content as TextContent).text + event.delta
          }
        });
      });
    }
    // Handle audio responses
    else if (event.type === 'response.audio.delta') {
      console.log(`[Event ${eventId}] Received audio delta:`, event);
      setHistory(prev => {
        const messageId = messageEventMap.current.get(event.item_id);
        if (!messageId) {
          console.warn(`[Event ${eventId}] No message found for audio delta`);
          return prev;
        }

        const message = prev.messages.find(msg => msg.id === messageId);
        if (!message || !isAudioContent(message.content)) {
          console.warn(`[Event ${eventId}] Message not found or not audio content`);
          return prev;
        }

        const updatedAudio = message.content.audio + event.delta;
        console.log(`[Event ${eventId}] Updating message ${messageId} with audio delta`);
        
        return updateMessage(prev, messageId, {
          content: {
            type: 'audio',
            audio: updatedAudio,
            transcript: message.content.transcript
          }
        });
      });
    }
    // Handle audio transcripts
    else if (event.type === 'response.audio_transcript.delta') {
      console.log(`[Event ${eventId}] Received transcript delta:`, event.delta);
      
      setHistory(prev => {
        const messageId = messageEventMap.current.get(event.item_id);
        if (!messageId) {
          console.warn(`[Event ${eventId}] No message found for transcript delta`);
          return prev;
        }

        const message = prev.messages.find(msg => msg.id === messageId);
        if (!message || !isAudioContent(message.content)) {
          console.warn(`[Event ${eventId}] Message not found or not audio content`);
          return prev;
        }

        const updatedTranscript = (message.content.transcript || '') + event.delta;
        console.log(`[Event ${eventId}] Updating message ${messageId} with transcript:`, updatedTranscript);

        return updateMessage(prev, messageId, {
          content: {
            ...message.content,
            transcript: updatedTranscript
          }
        });
      });
    }
    else if (event.type === 'response.audio_transcript.done') {
      console.log(`[Event ${eventId}] Received final transcript:`, event.transcript);
      
      setHistory(prev => {
        const messageId = messageEventMap.current.get(event.item_id);
        if (!messageId) {
          console.warn(`[Event ${eventId}] No message found for transcript`);
          return prev;
        }

        const message = prev.messages.find(msg => msg.id === messageId);
        if (!message || !isAudioContent(message.content)) {
          return prev;
        }

        return updateMessage(prev, messageId, {
          content: {
            ...message.content,
            transcript: event.transcript
          }
        });
      });
    }
    // Handle function calls
    else if (event.type === 'response.function_call_arguments.done') {
      console.log(`[Event ${eventId}] Received function call:`, event.name, event.arguments);
      setHistory(prev => addMessage(prev, {
        type: 'function_call',
        role: 'assistant',
            status: 'completed' as const,
            content: {
              type: 'function_call',
          name: event.name,
          arguments: event.arguments
        }
      }));

      handleFunctionCall(dataChannelRef.current!, event, tools, (result) => {
        console.log(`[Event ${eventId}] Adding function result to history:`, result);
        setHistory(prev => addMessage(prev, {
          type: 'message',
          role: 'tool',
            status: 'completed' as const,
            content: {
              type: 'text',
            text: JSON.stringify(result)
          },
          tool_call_id: event.call_id,
          name: event.name
        }));
      });
    }
    // Handle user audio transcription
    else if (event.type === 'conversation.item.input_audio_transcription.completed') {
      console.log(`[Event ${eventId}] User audio transcription completed:`, event.transcript);
      setHistory(prev => {
        const messageId = messageEventMap.current.get(event.item_id);
        if (!messageId) {
          console.warn(`[Event ${eventId}] No message found for transcription`);
          return prev;
        }

        const message = prev.messages.find(msg => msg.id === messageId);
        if (!message || !isAudioContent(message.content)) {
          console.warn(`[Event ${eventId}] Message not found or not audio content`);
          return prev;
        }

        return updateMessage(prev, messageId, {
          content: {
            type: 'audio',
            audio: message.content.audio,
            transcript: 'transcript' in event ? event.transcript as string : ''
          }
        });
      });
    }
    // Handle session updates
    else if (event.type === 'session.updated') {
      console.log(`[Event ${eventId}] Session update confirmed`);
    }
    // Handle completion
    else if (event.type === 'response.done') {
      console.log(`[Event ${eventId}] Response completed`);
      setHistory(prev => {
        const messages = prev.messages.map(msg => 
          msg.status === 'in_progress' ? { ...msg, status: 'completed' as const } : msg
        );
        return { ...prev, messages };
      });
      // Only clear message map entries for completed messages
      const completedItemIds = Array.from(messageEventMap.current.entries())
        .filter(([itemId, messageId]) => {
          const message = history.messages.find(msg => msg.id === messageId);
          return message && message.status === 'completed';
        })
        .map(([itemId]) => itemId);
      
      completedItemIds.forEach(itemId => {
        messageEventMap.current.delete(itemId);
      });
    }
  }, [tools]);

  const connect = useCallback(async () => {
    if (peerConnectionRef.current) return;

    try {
      // Get session URL from environment or use default
      const sessionUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/session';
      
      const response = await fetch(sessionUrl, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error('Failed to get session token');
      }

      const data = await response.json();
      const ephemeralKey = data.client_secret.value;

      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      pc.onconnectionstatechange = () => {
        console.log(`[WebRTC] Connection state changed: ${pc.connectionState}`);
      };

      pc.oniceconnectionstatechange = () => {
        console.log(`[WebRTC] ICE connection state changed: ${pc.iceConnectionState}`);
      };

      pc.ontrack = (e) => {
        if (audioRef.current) {
          audioRef.current.srcObject = e.streams[0];
          onRemoteStream?.(e.streams[0]);
        }
      };

      const dc = pc.createDataChannel('oai-events');
      dataChannelRef.current = dc;

      dc.onopen = async () => {
        console.log('[WebRTC] Data channel opened');
        
        try {
          // Wait a bit before sending session config
          console.log('Waiting before session config...');
          // await new Promise(resolve => setTimeout(resolve, 500));
          
          console.log('Updating session config...');
          sendEvent({
            type: 'session.update',
            session: {
              ...history.session_config,
              modalities: ['text', 'audio'] //keep hardcoded for now
            }
          });
          console.log('Session config update sent');

          // Wait for session update to be processed
          console.log('Waiting for session update to process...');
          // await new Promise(resolve => setTimeout(resolve, 1000));

          console.log('Loading history...');
          loadHistory(history);
          console.log('History load initiated');

          // Wait for history to be loaded
          console.log('Waiting for history to load...');
          // await new Promise(resolve => setTimeout(resolve, 1000));

          // Send initial message after session is set up
          console.log('Sending initial message...');
          
          sendTextMessage("<Ding dong!>");
          // sendTextMessage(tocho);
          console.log('Initial message sent');
        } catch (error) {
          console.error('Error in data channel open handler:', error);
        }
      };
      
      dc.onclose = () => {
        console.log('[WebRTC] Data channel closed');
      };

      dc.onerror = (error) => {
        console.error('[WebRTC] Data channel error:', error);
      };

      dc.addEventListener('message', handleEvent);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => {
          pc.addTrack(track, stream);
        });
        onLocalStream?.(stream);
      } catch (err) {
        console.error('Error accessing microphone:', err);
      }

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpResponse = await fetch(`${API_BASE_URL}?model=${history.session_config.model}`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          'Content-Type': 'application/sdp'
        },
      });

      if (!sdpResponse.ok) {
        throw new Error('Failed to establish WebRTC connection');
      }

      console.log('Got SDP response, creating answer');
      const answer = {
        type: 'answer' as RTCSdpType,
        sdp: await sdpResponse.text(),
      };

      console.log('Setting remote description');
      await pc.setRemoteDescription(answer);
      
      console.log('Checking data channel state:', dc.readyState);
      if (dc.readyState !== 'open') {
        console.log('Data channel not open, waiting...');
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Data channel open timeout')), 5000);
          dc.addEventListener('open', () => {
            clearTimeout(timeout);
            resolve();
          }, { once: true });
        });
      }

      console.log('Connection established, setting connected state');
      setIsConnected(true);
      
    } catch (error) {
      console.error('Connection error:', error);
      disconnect();
    }

  }, [initialInstructions, tools, onLocalStream, onRemoteStream, handleEvent, history, loadHistory]);

  const disconnect = useCallback(async () => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.getSenders().forEach(sender => {
        if (sender.track) {
          sender.track.stop();
        }
      });
    }
    
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }

    messageEventMap.current.clear();
    audioBufferRef.current = '';
    peerConnectionRef.current = null;
    dataChannelRef.current = null;
    setIsConnected(false);

    console.log(`[Session ${crypto.randomUUID()}] Disconnected`);
  }, []);

  const sendTextMessage = useCallback((text: string) => {
    // if (!isConnected || !text.trim()) {
    //   console.warn('Cannot send message: not connected or empty text');
    //   return;
    // }

    const message: Message = {
      id: `msg_${Math.random().toString(36).substring(2, 15)}`,
      type: 'message',
      role: 'user',
      status: 'completed' as const,
      content: {
        type: 'input_text',
        text: text.trim()
      },
      timestamp: new Date().toISOString()
    };

    sendEvent({
      type: 'conversation.item.create',
      item: toApiMessage(message)
    });

    sendEvent({
      type: 'response.create'
    });
  }, [isConnected, sendEvent]);

  useEffect(() => {
    if (!isConnected) {
      setRealtimeEvents([]);
    }
  }, [isConnected]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    isMuted,
    setIsMuted,
    connect,
    disconnect,
    sendTextMessage,
    history,
    setHistory,
    realtimeEvents
  };
}
